// weekly_planner.ts — Інтерактивний timeline планувальник тижневого графіка v2
// Dual view: Week Overview ↔ Day Timeline с drag & drop, resize, split, find slot

export interface PlannerTask {
    id: string;
    parentId?: string; // Для розділених задач — ID батьківської
    dayIndex: number; // 0=Пн..6=Нд, -1=Пул боргу
    startTime: string; // '18:30'
    endTime: string; // '22:30'
    durationMins: number; // 240
    title: string;
    category: 'delivery' | 'school' | 'pum' | 'sleep' | 'commute' | 'meal' | 'routine' | 'sport' | 'other';
    isFixed: boolean;
    allowSplit: boolean;
    minStart?: string;
    maxEnd?: string;
    priority: 'high' | 'medium' | 'low';
    status: 'planned' | 'in_progress' | 'completed' | 'missed' | 'needs_reschedule';
    notes?: string;
    weatherAlert?: string;
}

export interface FreeSlot {
    dayIndex: number;
    startMins: number;
    endMins: number;
    startTime: string;
    endTime: string;
    durationMins: number;
}

export interface CandidateSlot {
    dayIndex: number;
    dayName: string;
    startTime: string;
    endTime: string;
    durationMins: number;
    isPeak: boolean;
    isBuffer: boolean;
    gapDurationMins: number;
    description: string;
}

const DAYS_UK = ['Понеділок', 'Вівторок', 'Середа', 'Четвер', "П'ятниця", 'Субота', 'Неділя'];
const DAYS_SHORT = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'];

const PX_PER_MIN = 1.15; // 1 хвилина = 1.15px висоти на timeline
const TIMELINE_START_HOUR = 8; // Робочий день починається з 08:00 (сон 00:00-09:00 закріплений зверху)
const TIMELINE_END_HOUR = 24;
const SNAP_MINS = 15; // Snap до 15 хвилин

let plannerSettings = {
    saturdayPeakStart: '17:00',
    saturdayPeakEnd: '22:00',
    deliveryWeeklyQuota: 25.0
};

// Стан планувальника
let tasks: PlannerTask[] = [];
let currentView: 'week' | 'day' = 'day'; // За замовчуванням відкриваємо день (фокус на сьогодні)
let selectedDayIndex: number = -1; // Обраний день для detail view
let draggedTaskId: string | null = null;
let isDraggingOnTimeline = false;
let isResizing = false;
let resizeEdge: 'top' | 'bottom' | null = null;
let resizeTaskId: string | null = null;
let resizeStartY = 0;
let resizeOriginalMins = 0;
let dragStartY = 0;
let dragOriginalStartMins = 0;

let activeWeatherAlert: { dayIndex: number; text: string; taskTitle: string } | null = {
    dayIndex: 0,
    text: 'Очікується злива та шквал вітру. Доставка на двоколісному під ризиком.',
    taskTitle: 'Доставка (Glovo + Bolt)'
};

// ==================== УТИЛІТИ ====================
function timeToMins(timeStr: string): number {
    if (!timeStr) return 0;
    if (timeStr === '24:00' || timeStr === '00:00_end') return 1440;
    const [h, m] = timeStr.split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
}

function minsToTime(mins: number): string {
    const clamped = Math.max(0, Math.min(1440, mins));
    if (clamped === 1440) return '24:00';
    const h = Math.floor(clamped / 60);
    const m = clamped % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function formatDuration(mins: number): string {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h > 0 && m > 0) return `${h} год ${m} хв`;
    if (h > 0) return `${h} год`;
    return `${m} хв`;
}

function snapToGrid(mins: number): number {
    return Math.round(mins / SNAP_MINS) * SNAP_MINS;
}

function minsToY(mins: number): number {
    return (mins - TIMELINE_START_HOUR * 60) * PX_PER_MIN;
}

function yToMins(y: number): number {
    return snapToGrid(y / PX_PER_MIN + TIMELINE_START_HOUR * 60);
}

function getCategoryColor(cat: string): string {
    const map: Record<string, string> = {
        delivery: '#f97316', school: '#f59e0b', pum: '#0ea5e9',
        sleep: '#8b5cf6', commute: '#64748b', meal: '#10b981',
        routine: '#3b82f6', sport: '#a855f7', other: '#71717a'
    };
    return map[cat] || '#71717a';
}

function getCategoryIcon(cat: string): string {
    const map: Record<string, string> = {
        delivery: 'ri-riding-line', school: 'ri-book-read-line', pum: 'ri-macbook-line',
        sleep: 'ri-moon-clear-line', commute: 'ri-walk-line', meal: 'ri-restaurant-line',
        routine: 'ri-home-4-line', sport: 'ri-run-line', other: 'ri-task-line'
    };
    return map[cat] || 'ri-task-line';
}

// ==================== БІЗНЕС-ЛОГІКА ====================
export function createDefaultSchedule(): PlannerTask[] {
    const result: PlannerTask[] = [];

    for (let day = 0; day < 5; day++) {
        result.push(
            { id: `sleep_${day}`, dayIndex: day, startTime: '00:00', endTime: '09:00', durationMins: 540, title: 'Сон (Залізне вікно)', category: 'sleep', isFixed: true, allowSplit: false, priority: 'high', status: 'planned' },
            { id: `routine_morning_${day}`, dayIndex: day, startTime: '09:00', endTime: '10:30', durationMins: 90, title: 'Ранок, побут & сніданок', category: 'routine', isFixed: false, allowSplit: false, priority: 'medium', status: 'planned' },
            { id: `commute_pum_${day}`, dayIndex: day, startTime: '10:30', endTime: '11:00', durationMins: 30, title: 'Дорога до ПУМ', category: 'commute', isFixed: false, allowSplit: false, priority: 'medium', status: 'planned' },
            { id: `pum_${day}`, dayIndex: day, startTime: '11:00', endTime: '12:30', durationMins: 90, title: 'ПУМ (технік)', category: 'pum', isFixed: true, allowSplit: false, priority: 'high', status: 'planned' },
            { id: `lunch_${day}`, dayIndex: day, startTime: '12:30', endTime: '13:00', durationMins: 30, title: 'Обід & перепочинок', category: 'meal', isFixed: false, allowSplit: false, priority: 'low', status: 'planned' },
            { id: `commute_school_${day}`, dayIndex: day, startTime: '13:00', endTime: '13:30', durationMins: 30, title: 'Дорога до ліцею', category: 'commute', isFixed: false, allowSplit: false, priority: 'medium', status: 'planned' },
            { id: `school_${day}`, dayIndex: day, startTime: '13:30', endTime: '18:00', durationMins: 270, title: 'Школа / Ліцей (інклюзія)', category: 'school', isFixed: true, allowSplit: false, priority: 'high', status: 'planned' },
            { id: `commute_delivery_${day}`, dayIndex: day, startTime: '18:00', endTime: '18:30', durationMins: 30, title: 'Перехід на точку замовлень', category: 'commute', isFixed: false, allowSplit: false, priority: 'medium', status: 'planned' },
            { id: `delivery_${day}`, dayIndex: day, startTime: '18:30', endTime: '22:30', durationMins: 240, title: 'Доставка (Glovo + Bolt)', category: 'delivery', isFixed: false, allowSplit: true, minStart: '17:00', maxEnd: '23:00', priority: 'medium', status: 'planned' },
            { id: `dinner_evening_${day}`, dayIndex: day, startTime: '22:30', endTime: '24:00', durationMins: 90, title: 'Вечеря, душ, особистий час', category: 'routine', isFixed: false, allowSplit: false, priority: 'medium', status: 'planned' }
        );
    }

    result.push(
        { id: 'sleep_sat', dayIndex: 5, startTime: '00:00', endTime: '09:00', durationMins: 540, title: 'Сон (Залізне вікно)', category: 'sleep', isFixed: true, allowSplit: false, priority: 'high', status: 'planned' },
        { id: 'delivery_sat', dayIndex: 5, startTime: '17:00', endTime: '22:00', durationMins: 300, title: 'Доставка (Glovo + Bolt)', category: 'delivery', isFixed: false, allowSplit: true, minStart: '12:00', maxEnd: '23:00', priority: 'medium', status: 'planned', notes: 'Суботній піковий блок замовлень (5 год)' },
        { id: 'dinner_sat', dayIndex: 5, startTime: '22:00', endTime: '24:00', durationMins: 120, title: 'Вечеря, відпочинок, вільний час', category: 'routine', isFixed: false, allowSplit: false, priority: 'low', status: 'planned' }
    );

    result.push(
        { id: 'sleep_sun', dayIndex: 6, startTime: '00:00', endTime: '09:00', durationMins: 540, title: 'Сон (Залізне вікно)', category: 'sleep', isFixed: true, allowSplit: false, priority: 'high', status: 'planned' }
    );

    return result;
}

export function getFreeSlotsForDay(dayIndex: number): FreeSlot[] {
    const dayTasks = tasks
        .filter(t => t.dayIndex === dayIndex && t.status !== 'needs_reschedule')
        .sort((a, b) => timeToMins(a.startTime) - timeToMins(b.startTime));

    const freeSlots: FreeSlot[] = [];
    let currentMins = 0;

    for (const task of dayTasks) {
        const taskStart = timeToMins(task.startTime);
        const taskEnd = timeToMins(task.endTime);
        if (taskStart > currentMins) {
            const gap = taskStart - currentMins;
            if (gap >= 15) {
                freeSlots.push({ dayIndex, startMins: currentMins, endMins: taskStart, startTime: minsToTime(currentMins), endTime: minsToTime(taskStart), durationMins: gap });
            }
        }
        currentMins = Math.max(currentMins, taskEnd);
    }

    if (currentMins < 1440) {
        const gap = 1440 - currentMins;
        if (gap >= 15) {
            freeSlots.push({ dayIndex, startMins: currentMins, endMins: 1440, startTime: minsToTime(currentMins), endTime: '24:00', durationMins: gap });
        }
    }

    return freeSlots;
}

export function canPlaceTask(dayIndex: number, startMins: number, durationMins: number, excludeTaskId: string | null = null): { ok: boolean; reason?: string } {
    const endMins = startMins + durationMins;
    if (endMins > 1440) return { ok: false, reason: 'Задача виходить за межі доби (24:00)' };

    const dayTasks = tasks.filter(t => t.dayIndex === dayIndex && t.id !== excludeTaskId && t.status !== 'needs_reschedule');

    for (const other of dayTasks) {
        const otherStart = timeToMins(other.startTime);
        const otherEnd = timeToMins(other.endTime);
        if (startMins < otherEnd && endMins > otherStart) {
            return { ok: false, reason: `Конфлікт із «${other.title}» (${other.startTime}–${other.endTime})` };
        }
    }

    return { ok: true };
}

export function findAvailableSlots(task: PlannerTask): CandidateSlot[] {
    const candidates: CandidateSlot[] = [];
    const duration = task.durationMins;

    for (let day = 0; day < 7; day++) {
        const freeSlots = getFreeSlotsForDay(day);

        for (const slot of freeSlots) {
            let usableStart = Math.max(slot.startMins, 540); // Від 09:00
            let usableEnd = slot.endMins;

            if (usableEnd - usableStart >= duration) {
                const check = canPlaceTask(day, usableStart, duration, task.id);
                if (check.ok) {
                    const isPeak = day === 5 && task.category === 'delivery' && usableStart >= timeToMins(plannerSettings.saturdayPeakStart);
                    const isBuffer = day === 6;
                    candidates.push({
                        dayIndex: day,
                        dayName: DAYS_UK[day],
                        startTime: minsToTime(usableStart),
                        endTime: minsToTime(usableStart + duration),
                        durationMins: duration,
                        isPeak,
                        isBuffer,
                        gapDurationMins: slot.durationMins,
                        description: isPeak ? '⭐ Пікові години доставки' : isBuffer ? '🌿 Буфер відновлення' : `Вільне вікно на ${formatDuration(slot.durationMins)}`
                    });
                }
            }
        }
    }

    const seen = new Set<string>();
    return candidates.filter(c => {
        const key = `${c.dayIndex}_${c.startTime}_${c.endTime}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

// ==================== ЗБЕРЕЖЕННЯ / ЗАВАНТАЖЕННЯ ====================
function jsonParseSafe(str: string): any {
    try { return JSON.parse(str); } catch { return null; }
}

export async function loadScheduleData() {
    try {
        const res = await fetch('api/slots.php?action=get_schedule');
        if (res.ok) {
            const data = await res.json();
            if (data.success && data.schedule && Array.isArray(data.schedule.tasks)) {
                tasks = data.schedule.tasks;
                if (data.schedule.settings) plannerSettings = { ...plannerSettings, ...data.schedule.settings };
                renderWeeklyPlanner();
                return;
            }
        }
    } catch (e) {
        console.warn('API get_schedule failed, using local storage or template', e);
    }

    const saved = localStorage.getItem('syncflow_weekly_schedule_v2');
    if (saved) {
        try {
            const parsed = jsonParseSafe(saved);
            if (parsed && Array.isArray(parsed.tasks)) {
                tasks = parsed.tasks;
                renderWeeklyPlanner();
                return;
            }
        } catch (e) { /* fallback */ }
    }

    tasks = createDefaultSchedule();
    saveScheduleDataLocally();
    renderWeeklyPlanner();
}

export async function saveScheduleData() {
    saveScheduleDataLocally();
    try {
        await fetch('api/slots.php?action=save_schedule', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ schedule: { tasks, settings: plannerSettings, updated_at: new Date().toISOString() } })
        });
    } catch (e) {
        console.error('Failed to sync schedule with server', e);
    }
}

function saveScheduleDataLocally() {
    localStorage.setItem('syncflow_weekly_schedule_v2', JSON.stringify({ tasks, settings: plannerSettings }));
}

// ==================== СТАТИСТИКА ====================
function getDayStats(dayIndex: number) {
    const dayTasks = tasks.filter(t => t.dayIndex === dayIndex && t.status !== 'needs_reschedule');
    let workMins = 0, commuteMins = 0, routineMins = 0, sleepMins = 0;

    for (const t of dayTasks) {
        if (t.category === 'school' || t.category === 'pum' || t.category === 'delivery') workMins += t.durationMins;
        else if (t.category === 'commute') commuteMins += t.durationMins;
        else if (t.category === 'routine' || t.category === 'meal' || t.category === 'sport') routineMins += t.durationMins;
        else if (t.category === 'sleep') sleepMins += t.durationMins;
    }

    const scheduledMins = workMins + commuteMins + routineMins + sleepMins;
    const freeMins = Math.max(0, 1440 - scheduledMins);

    return {
        workMins, commuteMins, routineMins, sleepMins, scheduledMins, freeMins,
        workH: (workMins / 60).toFixed(1), commuteH: (commuteMins / 60).toFixed(1),
        routineH: (routineMins / 60).toFixed(1), sleepH: (sleepMins / 60).toFixed(1),
        freeH: (freeMins / 60).toFixed(1), scheduledH: (scheduledMins / 60).toFixed(1),
        taskCount: dayTasks.length
    };
}

function getWeekDeliverySummary() {
    const deliveryTasks = tasks.filter(t => t.category === 'delivery');
    let plannedHours = 0, completedHours = 0, debtHours = 0;

    for (const t of deliveryTasks) {
        const hours = t.durationMins / 60;
        if (t.dayIndex === -1 || t.status === 'needs_reschedule' || t.status === 'missed') debtHours += hours;
        else if (t.status === 'completed') { completedHours += hours; plannedHours += hours; }
        else plannedHours += hours;
    }

    return { planned: plannedHours, completed: completedHours, debt: debtHours, quota: plannerSettings.deliveryWeeklyQuota };
}

// ==================== ГОЛОВНИЙ РЕНДЕР ====================
export function renderWeeklyPlanner() {
    const container = document.getElementById('weekly-planner-root');
    if (!container) return;

    const todayDayIndex = (new Date().getDay() + 6) % 7;
    const unassignedTasks = tasks.filter(t => t.dayIndex === -1 || t.status === 'needs_reschedule');

    // Auto-select today if nothing selected
    if (selectedDayIndex < 0) selectedDayIndex = todayDayIndex;

    let html = `<div class="wp2-container">`;

    // Top bar
    html += renderTopBar(unassignedTasks.length);

    // Weather alert
    if (activeWeatherAlert) {
        html += `
        <div class="wp2-alert">
            <div class="wp2-alert-text">
                <i class="ri-rainy-line" style="font-size:18px;color:#38bdf8;"></i>
                <span><strong>Погода на ${DAYS_UK[activeWeatherAlert.dayIndex]}:</strong> ${activeWeatherAlert.text}</span>
            </div>
            <button class="wp2-btn wp2-btn-danger" onclick="rescheduleWeatherTask(${activeWeatherAlert.dayIndex})">
                <i class="ri-arrow-right-line"></i> Перенести доставку
            </button>
        </div>`;
    }

    // Debt pool
    if (unassignedTasks.length > 0) {
        html += renderDebtPool(unassignedTasks);
    }

    // Horizontal Day Strip
    html += renderWeekDayStrip(todayDayIndex);

    // Day detail view (or week grid)
    if (currentView === 'day') {
        html += renderDayDetail(selectedDayIndex, todayDayIndex);
    } else {
        html += renderWeekGrid(todayDayIndex);
    }

    html += `</div>`;

    container.innerHTML = html;

    // Initialize interactions
    if (currentView === 'day') {
        setupTimelineDragAndDrop();
        setupTimelineResize();
        scrollToCurrentTime();
    }
    setupWeekDragDrop();
}

function renderTopBar(debtCount: number): string {
    return `
    <div class="wp2-topbar">
        <div class="wp2-topbar-left">
            <span class="wp2-title">
                <i class="ri-calendar-schedule-line" style="color:var(--brand-primary,#3b82f6)"></i>
                Часові Блоки Дня
            </span>
            <div class="wp2-view-toggle">
                <button class="wp2-view-btn ${currentView === 'day' ? 'active' : ''}" onclick="setPlannerView('day')">
                    <i class="ri-layout-column-line"></i> День
                </button>
                <button class="wp2-view-btn ${currentView === 'week' ? 'active' : ''}" onclick="setPlannerView('week')">
                    <i class="ri-layout-grid-line"></i> Тиждень
                </button>
            </div>
            ${debtCount > 0 ? `<span class="wp2-debt-badge"><i class="ri-alarm-warning-fill"></i> Борг: ${debtCount}</span>` : ''}
        </div>
        <div class="wp2-topbar-right">
            <button class="wp2-btn" onclick="applyBaseScheduleTemplate()" data-tooltip="Застосувати базовий тижневий шаблон" data-tooltip-pos="bottom">
                <i class="ri-history-line"></i> Шаблон
            </button>
            <button class="wp2-btn wp2-btn-primary" onclick="openAddTaskModal()">
                <i class="ri-add-line"></i> + Додати задачу
            </button>
        </div>
    </div>`;
}

function renderDebtPool(unassigned: PlannerTask[]): string {
    return `
    <div class="wp2-debt-pool">
        <div class="wp2-debt-header">
            <span class="wp2-debt-title">
                <i class="ri-alarm-warning-fill"></i> Потребують перенесення (${unassigned.length})
            </span>
            <button class="wp2-btn wp2-btn-sm wp2-btn-magic" onclick="autoRescheduleDebtTasks()" data-tooltip="Автоматично розмістити у найближчі вільні слоти" data-tooltip-pos="bottom">
                <i class="ri-magic-line"></i> Авто-розміщення
            </button>
        </div>
        <div class="wp2-debt-cards">
            ${unassigned.map(t => `
                <div class="wp2-debt-card" draggable="true" data-task-id="${t.id}">
                    <div class="wp2-debt-card-title">
                        <i class="${getCategoryIcon(t.category)}" style="color:${getCategoryColor(t.category)}"></i>
                        <span>${t.title}</span>
                    </div>
                    <div class="wp2-debt-card-meta">
                        <i class="ri-time-line"></i> ${formatDuration(t.durationMins)}
                    </div>
                    <div class="wp2-debt-card-actions">
                        <button class="wp2-btn wp2-btn-sm" onclick="openFindSlotModal('${t.id}')"><i class="ri-search-eye-line"></i> Знайти слот</button>
                        ${t.allowSplit || t.durationMins >= 120 ? `<button class="wp2-btn wp2-btn-sm" onclick="openSplitTaskModal('${t.id}')" title="Розділити навпіл"><i class="ri-scissors-cut-line"></i></button>` : ''}
                        <button class="wp2-btn wp2-btn-sm wp2-btn-danger" onclick="dismissDebtTask('${t.id}')"><i class="ri-close-line"></i></button>
                    </div>
                </div>
            `).join('')}
        </div>
    </div>`;
}

// ==================== WEEK DAY STRIP ====================
function renderWeekDayStrip(todayIdx: number): string {
    let html = `<div class="wp2-day-strip" id="wp2-day-strip">`;

    for (let day = 0; day < 7; day++) {
        const stats = getDayStats(day);
        const isToday = day === todayIdx;
        const isSelected = currentView === 'day' && day === selectedDayIndex;
        const fillPct = Math.min(100, Math.round((stats.scheduledH / 15) * 100));

        html += `
        <div class="wp2-day-strip-item ${isToday ? 'is-today' : ''} ${isSelected ? 'is-selected' : ''}" 
             data-day-index="${day}" 
             onclick="selectDay(${day})"
             id="wp2-strip-day-${day}">
            <div class="wp2-strip-header">
                <span class="wp2-strip-name">${DAYS_SHORT[day]}</span>
                ${isToday ? '<span class="wp2-strip-badge-today">Сьогодні</span>' : ''}
                ${day === 6 ? '<span class="wp2-strip-badge-weekend">Вихідний</span>' : ''}
            </div>
            <div class="wp2-strip-body">
                <span class="wp2-strip-hours">${stats.scheduledH.toFixed(1)} год</span>
                <span class="wp2-strip-free">+${stats.freeH.toFixed(1)}г вільно</span>
            </div>
            <div class="wp2-strip-progress">
                <div class="wp2-strip-progress-bar" style="width:${fillPct}%"></div>
            </div>
        </div>`;
    }

    html += `</div>`;
    return html;
}

// ==================== WEEK GRID (ALTERNATIVE VIEW) ====================
function renderWeekGrid(todayIdx: number): string {
    let html = `<div class="wp2-week-grid" id="wp2-week-grid">`;

    for (let day = 0; day < 7; day++) {
        const stats = getDayStats(day);
        const isToday = day === todayIdx;
        const dayTasks = tasks
            .filter(t => t.dayIndex === day && t.status !== 'needs_reschedule' && t.category !== 'sleep')
            .sort((a, b) => timeToMins(a.startTime) - timeToMins(b.startTime));

        html += `
        <div class="wp2-grid-col ${isToday ? 'is-today' : ''}" data-day-index="${day}">
            <div class="wp2-grid-col-header" onclick="selectDay(${day})">
                <span class="wp2-grid-day-name">${DAYS_SHORT[day]}</span>
                ${isToday ? '<span class="wp2-strip-badge-today">Сьогодні</span>' : ''}
                <span class="wp2-grid-day-hours">${stats.scheduledH.toFixed(1)} год</span>
            </div>
            <div class="wp2-grid-col-body">
                ${dayTasks.map(t => {
                    const color = getCategoryColor(t.category);
                    return `
                    <div class="wp2-grid-task" style="border-left:3px solid ${color};background:${color}15;" onclick="selectDay(${day})">
                        <div class="wp2-grid-task-time">${t.startTime}–${t.endTime}</div>
                        <div class="wp2-grid-task-title">${t.title}</div>
                    </div>`;
                }).join('')}
                <button class="wp2-grid-add-btn" onclick="openAddTaskModal();">+ Додати</button>
            </div>
        </div>`;
    }

    html += `</div>`;
    return html;
}

// ==================== DAY DETAIL VIEW ====================
function renderDayDetail(dayIdx: number, todayIdx: number): string {
    const stats = getDayStats(dayIdx);
    const isToday = dayIdx === todayIdx;
    const dayTasks = tasks
        .filter(t => t.dayIndex === dayIdx && t.status !== 'needs_reschedule' && t.category !== 'sleep')
        .sort((a, b) => timeToMins(a.startTime) - timeToMins(b.startTime));
    const freeSlots = getFreeSlotsForDay(dayIdx).filter(s => s.endMins > TIMELINE_START_HOUR * 60);

    return `
    <div class="wp2-day-detail-fullscreen">
        <!-- Sleep Rule Banner -->
        <div class="wp2-sleep-banner">
            <div class="wp2-sleep-banner-left">
                <div class="wp2-sleep-icon"><i class="ri-moon-clear-line"></i></div>
                <div>
                    <span class="wp2-sleep-title">Залізне вікно сну (недоторканне)</span>
                    <span class="wp2-sleep-desc">00:00 – 09:00 (9.0 год відновлення) · Жодних компромісів зі сном</span>
                </div>
            </div>
            <div class="wp2-sleep-banner-right">
                <span class="wp2-sleep-badge"><i class="ri-shield-check-line"></i> 100% Захищено</span>
            </div>
        </div>

        <div class="wp2-day-card">
            <div class="wp2-day-header">
                <div class="wp2-day-header-left">
                    <button class="wp2-btn wp2-btn-sm" onclick="navigateDay(-1)"><i class="ri-arrow-left-s-line"></i> Попередній</button>
                    <span class="wp2-day-name-big">${DAYS_UK[dayIdx]}</span>
                    ${isToday ? '<span class="wp2-week-day-badge wp2-badge-today">Сьогодні</span>' : ''}
                    <button class="wp2-btn wp2-btn-sm" onclick="navigateDay(1)">Наступний <i class="ri-arrow-right-s-line"></i></button>
                </div>
                <div class="wp2-day-header-stats">
                    <span class="wp2-day-header-stat">Заплановано: <strong>${stats.scheduledH} год</strong></span>
                    <span class="wp2-day-header-stat">Вільно: <strong style="color:var(--color-success,#10b981)">${stats.freeH} год</strong></span>
                    <span class="wp2-day-header-stat">Блоків: <strong>${dayTasks.length}</strong></span>
                </div>
            </div>

            <div class="wp2-timeline-wrapper" id="wp2-timeline-wrapper">
                <div style="position:relative;height:${(TIMELINE_END_HOUR - TIMELINE_START_HOUR) * 60 * PX_PER_MIN}px">
                    <!-- Hour labels and grid lines -->
                    <div class="wp2-timeline-hours">
                        ${renderHourLabels()}
                    </div>

                    <!-- Timeline area -->
                    <div class="wp2-timeline" id="wp2-timeline" data-day-index="${dayIdx}">
                        ${renderHourLines()}
                        ${isToday ? renderNowLine() : ''}
                        ${freeSlots.map(s => renderFreeBlock(s)).join('')}
                        ${dayTasks.map(t => renderTaskBlock(t)).join('')}
                    </div>
                </div>
            </div>
        </div>
    </div>`;
}

function renderHourLabels(): string {
    let html = '';
    for (let h = TIMELINE_START_HOUR; h <= TIMELINE_END_HOUR; h++) {
        const y = (h - TIMELINE_START_HOUR) * 60 * PX_PER_MIN;
        html += `<div class="wp2-hour-label" style="top:${y}px">${String(h).padStart(2, '0')}:00</div>`;
    }
    return html;
}

function renderHourLines(): string {
    let html = '';
    for (let h = TIMELINE_START_HOUR; h <= TIMELINE_END_HOUR; h++) {
        const y = (h - TIMELINE_START_HOUR) * 60 * PX_PER_MIN;
        html += `<div class="wp2-hour-line" style="top:${y}px"></div>`;
        if (h < TIMELINE_END_HOUR) {
            const halfY = y + 30 * PX_PER_MIN;
            html += `<div class="wp2-half-hour-line" style="top:${halfY}px"></div>`;
        }
    }
    return html;
}

function renderNowLine(): string {
    const now = new Date();
    const nowMins = now.getHours() * 60 + now.getMinutes();
    if (nowMins < TIMELINE_START_HOUR * 60 || nowMins > TIMELINE_END_HOUR * 60) return '';
    const y = minsToY(nowMins);
    return `
        <div class="wp2-now-line" style="top:${y}px"></div>
        <div class="wp2-now-dot" style="top:${y}px"><span>Зараз ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}</span></div>
    `;
}

function renderFreeBlock(slot: FreeSlot): string {
    const rawStart = Math.max(slot.startMins, TIMELINE_START_HOUR * 60);
    const rawEnd = Math.min(slot.endMins, TIMELINE_END_HOUR * 60);
    if (rawEnd <= rawStart) return '';

    const top = minsToY(rawStart);
    const duration = rawEnd - rawStart;
    const height = duration * PX_PER_MIN;
    if (height < 18) return '';

    const showText = height >= 28;

    return `
    <div class="wp2-free-block" 
         style="top:${top}px;height:${height}px"
         onclick="openQuickAddModal(${slot.dayIndex}, '${minsToTime(rawStart)}', ${duration})"
         data-free-start="${rawStart}" data-free-end="${rawEnd}"
         data-tooltip="Вільне вікно: ${formatDuration(duration)} (${minsToTime(rawStart)}–${minsToTime(rawEnd)}). Натисніть, щоб додати задачу."
         data-tooltip-pos="right">
        ${showText ? `
            <div class="wp2-free-inner">
                <span class="wp2-free-text"><i class="ri-add-circle-line"></i> Вільний час — ${formatDuration(duration)}</span>
                <span class="wp2-free-time">${minsToTime(rawStart)}–${minsToTime(rawEnd)}</span>
            </div>
        ` : ''}
    </div>`;
}

function renderTaskBlock(task: PlannerTask): string {
    const rawStart = Math.max(timeToMins(task.startTime), TIMELINE_START_HOUR * 60);
    const rawEnd = Math.min(timeToMins(task.endTime), TIMELINE_END_HOUR * 60);
    if (rawEnd <= rawStart) return '';

    const top = minsToY(rawStart);
    const height = (rawEnd - rawStart) * PX_PER_MIN;
    const isCompleted = task.status === 'completed';
    const isSmall = height < 55;
    const isTiny = height < 32;

    const resizeHandles = !task.isFixed ? `
        <div class="wp2-resize-top" data-resize-task="${task.id}" data-resize-edge="top"></div>
        <div class="wp2-resize-bottom" data-resize-task="${task.id}" data-resize-edge="bottom"></div>
    ` : '';

    let content = '';
    if (isTiny) {
        content = `
            <div class="wp2-block-top">
                <span class="wp2-block-time">${task.startTime}–${task.endTime}</span>
                <span class="wp2-block-title"><i class="${getCategoryIcon(task.category)}" style="color:${getCategoryColor(task.category)}"></i> ${task.title}</span>
            </div>
        `;
    } else if (isSmall) {
        content = `
            <div class="wp2-block-top">
                <span class="wp2-block-time">${task.startTime}–${task.endTime}</span>
                <span class="wp2-block-duration">${formatDuration(task.durationMins)}</span>
            </div>
            <div class="wp2-block-title">
                <i class="${getCategoryIcon(task.category)}" style="color:${getCategoryColor(task.category)}"></i>
                <span>${task.title}</span>
                ${task.isFixed ? '<i class="ri-lock-line wp2-fixed-icon" title="Фіксований"></i>' : ''}
            </div>
        `;
    } else {
        content = `
            <div class="wp2-block-top">
                <span class="wp2-block-time">${task.startTime}–${task.endTime}</span>
                <span class="wp2-block-duration">${formatDuration(task.durationMins)}</span>
            </div>
            <div class="wp2-block-title">
                <i class="${getCategoryIcon(task.category)}" style="color:${getCategoryColor(task.category)}"></i>
                <span>${task.title}</span>
                ${task.isFixed ? '<span class="wp2-task-badge-fixed"><i class="ri-lock-line"></i> Фікс.</span>' : '<span class="wp2-task-badge-flexible"><i class="ri-drag-move-fill"></i> Рухомий</span>'}
            </div>
            <div class="wp2-block-actions">
                ${!task.isFixed ? `
                    <button class="wp2-block-action act-complete ${isCompleted ? 'is-active' : ''}" onclick="event.stopPropagation();toggleTaskCompleted('${task.id}')" data-tooltip="${isCompleted ? 'Відновити' : 'Виконано'}" data-tooltip-pos="top">
                        <i class="${isCompleted ? 'ri-checkbox-circle-fill' : 'ri-checkbox-blank-circle-line'}"></i>
                    </button>
                    <button class="wp2-block-action act-reschedule" onclick="event.stopPropagation();openFindSlotModal('${task.id}')" data-tooltip="Знайти інший слот" data-tooltip-pos="top">
                        <i class="ri-calendar-line"></i> Знайти слот
                    </button>
                    ${task.allowSplit || task.durationMins >= 120 ? `
                        <button class="wp2-block-action act-split" onclick="event.stopPropagation();openSplitTaskModal('${task.id}')" data-tooltip="Розділити навпіл" data-tooltip-pos="top">
                            <i class="ri-scissors-cut-line"></i> Розділити
                        </button>
                    ` : ''}
                    <button class="wp2-block-action act-debt" onclick="event.stopPropagation();moveTaskToDebt('${task.id}')" data-tooltip="Перенести в борг" data-tooltip-pos="top">
                        <i class="ri-alarm-warning-line"></i>
                    </button>
                ` : `<span class="wp2-fixed-badge"><i class="ri-shield-keyhole-line"></i> Фіксований графік</span>`}
            </div>
        `;
    }

    return `
    <div class="wp2-task-block wp2-cat-${task.category} ${task.isFixed ? 'is-fixed' : ''} ${isCompleted ? 'status-completed' : ''}"
         style="top:${top}px;height:${height}px"
         draggable="${!task.isFixed}"
         data-task-id="${task.id}"
         id="wp2-task-${task.id}">
        ${resizeHandles}
        ${content}
    </div>`;
}

function renderDaySidebar(dayIdx: number, stats: any): string {
    const dayTasks = tasks
        .filter(t => t.dayIndex === dayIdx && t.status !== 'needs_reschedule')
        .sort((a, b) => timeToMins(a.startTime) - timeToMins(b.startTime));
    const freeSlots = getFreeSlotsForDay(dayIdx).filter(s => s.startMins >= 540);

    // Delivery stats
    const deliveryStats = getWeekDeliverySummary();

    return `
    <!-- Day stats -->
    <div class="wp2-sidebar-panel">
        <div class="wp2-sidebar-header">
            <i class="ri-bar-chart-box-line"></i> Баланс дня
        </div>
        <div class="wp2-sidebar-body">
            <div class="wp2-stats-grid">
                <div class="wp2-stat-item">
                    <span class="wp2-stat-label">💼 Робота</span>
                    <span class="wp2-stat-value">${stats.workH} год</span>
                </div>
                <div class="wp2-stat-item">
                    <span class="wp2-stat-label">🟢 Вільно</span>
                    <span class="wp2-stat-value" style="color:#34d399">${stats.freeH} год</span>
                </div>
                <div class="wp2-stat-item">
                    <span class="wp2-stat-label">🚗 Дорога</span>
                    <span class="wp2-stat-value">${stats.commuteH} год</span>
                </div>
                <div class="wp2-stat-item">
                    <span class="wp2-stat-label">🏠 Побут</span>
                    <span class="wp2-stat-value">${stats.routineH} год</span>
                </div>
            </div>
        </div>
    </div>

    <!-- Task list -->
    <div class="wp2-sidebar-panel">
        <div class="wp2-sidebar-header">
            <i class="ri-list-check-3"></i> Задачі дня (${dayTasks.length})
        </div>
        <div class="wp2-sidebar-body">
            ${dayTasks.filter(t => t.category !== 'sleep').map(t => `
                <div class="wp2-side-task" draggable="${!t.isFixed}" data-task-id="${t.id}" onclick="scrollToTask('${t.id}')">
                    <div class="wp2-side-task-dot" style="background:${getCategoryColor(t.category)}"></div>
                    <div class="wp2-side-task-info">
                        <span class="wp2-side-task-name">${t.title}</span>
                        <span class="wp2-side-task-time">${t.startTime}–${t.endTime}</span>
                    </div>
                    <span class="wp2-side-task-dur">${formatDuration(t.durationMins)}</span>
                </div>
            `).join('')}
            ${dayTasks.filter(t => t.category !== 'sleep').length === 0 ? '<span style="font-size:11px;color:var(--text-dim);text-align:center;padding:12px">Немає задач</span>' : ''}
        </div>
    </div>

    <!-- Free slots -->
    <div class="wp2-sidebar-panel">
        <div class="wp2-sidebar-header">
            <i class="ri-time-line" style="color:#34d399"></i> Вільні вікна (${freeSlots.length})
        </div>
        <div class="wp2-sidebar-body">
            ${freeSlots.map(s => `
                <div class="wp2-side-task" onclick="openQuickAddModal(${s.dayIndex}, '${s.startTime}', ${s.durationMins})" style="cursor:pointer">
                    <div class="wp2-side-task-dot" style="background:#34d399"></div>
                    <div class="wp2-side-task-info">
                        <span class="wp2-side-task-name" style="color:#34d399">Вільно</span>
                        <span class="wp2-side-task-time">${s.startTime}–${s.endTime}</span>
                    </div>
                    <span class="wp2-side-task-dur" style="color:#34d399">${formatDuration(s.durationMins)}</span>
                </div>
            `).join('')}
            ${freeSlots.length === 0 ? '<span style="font-size:11px;color:var(--text-dim);text-align:center;padding:12px">Немає вільних вікон після 09:00</span>' : ''}
        </div>
    </div>

    <!-- Weekly delivery -->
    <div class="wp2-sidebar-panel">
        <div class="wp2-sidebar-header">
            <i class="ri-riding-line" style="color:#f97316"></i> Доставка тижня
        </div>
        <div class="wp2-sidebar-body">
            <div class="wp2-stats-grid">
                <div class="wp2-stat-item">
                    <span class="wp2-stat-label">Заплановано</span>
                    <span class="wp2-stat-value">${deliveryStats.planned.toFixed(1)}г</span>
                </div>
                <div class="wp2-stat-item">
                    <span class="wp2-stat-label">Квота</span>
                    <span class="wp2-stat-value">${deliveryStats.quota}г</span>
                </div>
            </div>
            ${deliveryStats.debt > 0 ? `<span style="font-size:11px;color:#fca5a5;display:flex;align-items:center;gap:4px"><i class="ri-alarm-warning-line"></i> Борг: ${deliveryStats.debt.toFixed(1)} год</span>` : ''}
        </div>
    </div>`;
}

// ==================== TIMELINE DRAG & DROP ====================
function setupTimelineDragAndDrop() {
    const timeline = document.getElementById('wp2-timeline');
    if (!timeline) return;

    const taskBlocks = timeline.querySelectorAll('.wp2-task-block[draggable="true"]');

    taskBlocks.forEach(block => {
        block.addEventListener('mousedown', (e: any) => {
            // Ігноруємо якщо це resize handle або кнопка
            if (e.target.closest('.wp2-resize-top, .wp2-resize-bottom, .wp2-block-action, button')) return;

            const taskId = block.getAttribute('data-task-id');
            const task = tasks.find(t => t.id === taskId);
            if (!task || task.isFixed) return;

            isDraggingOnTimeline = true;
            draggedTaskId = taskId;
            dragStartY = e.clientY;
            dragOriginalStartMins = timeToMins(task.startTime);

            block.classList.add('is-dragging');

            const onMouseMove = (ev: MouseEvent) => {
                if (!isDraggingOnTimeline || !draggedTaskId) return;

                const deltaY = ev.clientY - dragStartY;
                const deltaMins = snapToGrid(deltaY / PX_PER_MIN);
                const newStartMins = Math.max(0, Math.min(1440 - task.durationMins, dragOriginalStartMins + deltaMins));

                // Update preview
                updateDragPreview(timeline, newStartMins, task.durationMins, task.id, task.dayIndex);

                // Move the block visually
                const el = document.getElementById(`wp2-task-${task.id}`);
                if (el) {
                    el.style.top = `${minsToY(newStartMins)}px`;
                }
            };

            const onMouseUp = (ev: MouseEvent) => {
                document.removeEventListener('mousemove', onMouseMove);
                document.removeEventListener('mouseup', onMouseUp);

                if (!isDraggingOnTimeline || !draggedTaskId) return;

                const deltaY = ev.clientY - dragStartY;
                const deltaMins = snapToGrid(deltaY / PX_PER_MIN);
                const newStartMins = Math.max(0, Math.min(1440 - task.durationMins, dragOriginalStartMins + deltaMins));

                // Validate placement
                const check = canPlaceTask(task.dayIndex, newStartMins, task.durationMins, task.id);
                if (check.ok && newStartMins !== dragOriginalStartMins) {
                    task.startTime = minsToTime(newStartMins);
                    task.endTime = minsToTime(newStartMins + task.durationMins);
                    saveScheduleData();
                    showPlannerToast(`«${task.title}» → ${task.startTime}–${task.endTime}`, 'success');
                } else if (!check.ok && newStartMins !== dragOriginalStartMins) {
                    showPlannerToast(check.reason || 'Конфлікт!', 'error');
                }

                isDraggingOnTimeline = false;
                draggedTaskId = null;
                removeDragPreview();
                renderWeeklyPlanner();
            };

            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
        });
    });
}

function updateDragPreview(timeline: HTMLElement, startMins: number, durationMins: number, excludeId: string, dayIndex: number) {
    removeDragPreview();

    const check = canPlaceTask(dayIndex, startMins, durationMins, excludeId);
    const top = minsToY(startMins);
    const height = durationMins * PX_PER_MIN;

    const preview = document.createElement('div');
    preview.id = 'wp2-drag-preview';
    preview.className = `wp2-drag-preview ${check.ok ? '' : 'is-conflict'}`;
    preview.style.top = `${top}px`;
    preview.style.height = `${height}px`;
    preview.textContent = `${minsToTime(startMins)}–${minsToTime(startMins + durationMins)}`;

    timeline.appendChild(preview);
}

function removeDragPreview() {
    const p = document.getElementById('wp2-drag-preview');
    if (p) p.remove();
}

// ==================== TIMELINE RESIZE ====================
function setupTimelineResize() {
    const handles = document.querySelectorAll('.wp2-resize-top, .wp2-resize-bottom');

    handles.forEach(handle => {
        handle.addEventListener('mousedown', (e: any) => {
            e.preventDefault();
            e.stopPropagation();

            const taskId = handle.getAttribute('data-resize-task');
            const edge = handle.getAttribute('data-resize-edge') as 'top' | 'bottom';
            const task = tasks.find(t => t.id === taskId);
            if (!task || task.isFixed) return;

            isResizing = true;
            resizeTaskId = taskId;
            resizeEdge = edge;
            resizeStartY = e.clientY;
            resizeOriginalMins = edge === 'top' ? timeToMins(task.startTime) : timeToMins(task.endTime);

            const onMouseMove = (ev: MouseEvent) => {
                if (!isResizing || !resizeTaskId) return;

                const deltaY = ev.clientY - resizeStartY;
                const deltaMins = snapToGrid(deltaY / PX_PER_MIN);

                const el = document.getElementById(`wp2-task-${task.id}`);
                if (!el) return;

                if (resizeEdge === 'top') {
                    const newStart = Math.max(0, resizeOriginalMins + deltaMins);
                    const endMins = timeToMins(task.endTime);
                    const newDuration = endMins - newStart;
                    if (newDuration >= 15 && newDuration <= 720) {
                        el.style.top = `${minsToY(newStart)}px`;
                        el.style.height = `${newDuration * PX_PER_MIN}px`;
                    }
                } else {
                    const startMins = timeToMins(task.startTime);
                    const newEnd = Math.min(1440, resizeOriginalMins + deltaMins);
                    const newDuration = newEnd - startMins;
                    if (newDuration >= 15 && newDuration <= 720) {
                        el.style.height = `${newDuration * PX_PER_MIN}px`;
                    }
                }
            };

            const onMouseUp = (ev: MouseEvent) => {
                document.removeEventListener('mousemove', onMouseMove);
                document.removeEventListener('mouseup', onMouseUp);

                if (!isResizing || !resizeTaskId) return;

                const deltaY = ev.clientY - resizeStartY;
                const deltaMins = snapToGrid(deltaY / PX_PER_MIN);

                if (resizeEdge === 'top') {
                    const newStart = Math.max(0, resizeOriginalMins + deltaMins);
                    const endMins = timeToMins(task.endTime);
                    const newDuration = endMins - newStart;
                    if (newDuration >= 15 && newDuration <= 720) {
                        const check = canPlaceTask(task.dayIndex, newStart, newDuration, task.id);
                        if (check.ok) {
                            task.startTime = minsToTime(newStart);
                            task.durationMins = newDuration;
                            saveScheduleData();
                            showPlannerToast(`«${task.title}» → ${task.startTime}–${task.endTime} (${formatDuration(task.durationMins)})`, 'success');
                        } else {
                            showPlannerToast(check.reason || 'Конфлікт!', 'error');
                        }
                    }
                } else {
                    const startMins = timeToMins(task.startTime);
                    const newEnd = Math.min(1440, resizeOriginalMins + deltaMins);
                    const newDuration = newEnd - startMins;
                    if (newDuration >= 15 && newDuration <= 720) {
                        const check = canPlaceTask(task.dayIndex, startMins, newDuration, task.id);
                        if (check.ok) {
                            task.endTime = minsToTime(newEnd);
                            task.durationMins = newDuration;
                            saveScheduleData();
                            showPlannerToast(`«${task.title}» → ${task.startTime}–${task.endTime} (${formatDuration(task.durationMins)})`, 'success');
                        } else {
                            showPlannerToast(check.reason || 'Конфлікт!', 'error');
                        }
                    }
                }

                isResizing = false;
                resizeTaskId = null;
                resizeEdge = null;
                renderWeeklyPlanner();
            };

            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
        });
    });
}

// ==================== WEEK DRAG & DROP (між днями) ====================
function setupWeekDragDrop() {
    // Debt cards draggable
    const debtCards = document.querySelectorAll('.wp2-debt-card[draggable="true"]');
    debtCards.forEach(card => {
        card.addEventListener('dragstart', (e: any) => {
            draggedTaskId = card.getAttribute('data-task-id');
            e.dataTransfer.setData('text/plain', draggedTaskId || '');
            e.dataTransfer.effectAllowed = 'move';
        });
        card.addEventListener('dragend', () => {
            draggedTaskId = null;
            clearWeekDropzones();
        });
    });

    // Week day drop zones
    const weekDays = document.querySelectorAll('.wp2-week-day');
    weekDays.forEach(col => {
        col.addEventListener('dragover', (e: any) => {
            e.preventDefault();
            if (!draggedTaskId) return;

            const dayIdx = Number(col.getAttribute('data-day-index'));
            const task = tasks.find(t => t.id === draggedTaskId);
            if (!task) return;

            const freeSlots = getFreeSlotsForDay(dayIdx);
            const canFit = freeSlots.some(s => s.durationMins >= task.durationMins);

            if (canFit) {
                col.classList.add('dropzone-valid');
                col.classList.remove('dropzone-invalid');
                e.dataTransfer.dropEffect = 'move';
            } else {
                col.classList.add('dropzone-invalid');
                col.classList.remove('dropzone-valid');
                e.dataTransfer.dropEffect = 'none';
            }
        });

        col.addEventListener('dragleave', (e: any) => {
            if (!col.contains(e.relatedTarget)) {
                col.classList.remove('dropzone-valid', 'dropzone-invalid');
            }
        });

        col.addEventListener('drop', (e: any) => {
            e.preventDefault();
            clearWeekDropzones();
            const taskId = e.dataTransfer.getData('text/plain') || draggedTaskId;
            if (!taskId) return;
            const dayIdx = Number(col.getAttribute('data-day-index'));
            handleDropTaskOnDay(taskId, dayIdx);
        });
    });
}

function clearWeekDropzones() {
    document.querySelectorAll('.wp2-week-day').forEach(c => {
        c.classList.remove('dropzone-valid', 'dropzone-invalid');
    });
}

function handleDropTaskOnDay(taskId: string, dayIndex: number) {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    if (task.isFixed) {
        showPlannerToast('Фіксовані задачі не можна переміщувати', 'warning');
        return;
    }

    const freeSlots = getFreeSlotsForDay(dayIndex);
    const suitableSlot = freeSlots.find(s => s.durationMins >= task.durationMins);

    if (!suitableSlot) {
        showPlannerToast(`У ${DAYS_UK[dayIndex]} немає вікна на ${formatDuration(task.durationMins)}`, 'error');
        return;
    }

    task.dayIndex = dayIndex;
    task.startTime = minsToTime(suitableSlot.startMins);
    task.endTime = minsToTime(suitableSlot.startMins + task.durationMins);
    task.status = 'planned';

    saveScheduleData();
    renderWeeklyPlanner();
    showPlannerToast(`«${task.title}» → ${DAYS_UK[dayIndex]} (${task.startTime}–${task.endTime})`, 'success');
}

// ==================== СКРОЛ ДО ПОТОЧНОГО ЧАСУ ====================
function scrollToCurrentTime() {
    const wrapper = document.getElementById('wp2-timeline-wrapper');
    if (!wrapper) return;
    const now = new Date();
    const nowMins = now.getHours() * 60 + now.getMinutes();
    const scrollTo = Math.max(0, minsToY(nowMins) - 200);
    setTimeout(() => { wrapper.scrollTop = scrollTo; }, 50);
}

function scrollToTask(taskId: string) {
    const el = document.getElementById(`wp2-task-${taskId}`);
    if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.style.outline = '2px solid var(--brand-primary)';
        setTimeout(() => { el.style.outline = 'none'; }, 1500);
    }
}

// ==================== GLOBAL FUNCTIONS ====================
(window as any).setPlannerView = function(view: 'week' | 'day') {
    currentView = view;
    renderWeeklyPlanner();
};

(window as any).selectDay = function(dayIdx: number) {
    selectedDayIndex = dayIdx;
    currentView = 'day';
    renderWeeklyPlanner();
};

(window as any).navigateDay = function(delta: number) {
    selectedDayIndex = Math.max(0, Math.min(6, selectedDayIndex + delta));
    renderWeeklyPlanner();
};

(window as any).scrollToTask = scrollToTask;

(window as any).toggleTaskCompleted = function(taskId: string) {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    task.status = task.status === 'completed' ? 'planned' : 'completed';
    saveScheduleData();
    renderWeeklyPlanner();
};

(window as any).moveTaskToDebt = function(taskId: string) {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    task.dayIndex = -1;
    task.status = 'needs_reschedule';
    saveScheduleData();
    renderWeeklyPlanner();
    showPlannerToast(`«${task.title}» → у борг`, 'info');
};

(window as any).dismissDebtTask = function(taskId: string) {
    tasks = tasks.filter(t => t.id !== taskId);
    saveScheduleData();
    renderWeeklyPlanner();
    showPlannerToast('Задачу видалено з боргу', 'info');
};

(window as any).applyBaseScheduleTemplate = function() {
    if (confirm('Скинути поточний тиждень та застосувати базовий шаблон графіка?')) {
        tasks = createDefaultSchedule();
        saveScheduleData();
        renderWeeklyPlanner();
        showPlannerToast('Базовий шаблон тижня застосовано', 'success');
    }
};

// ==================== МОДАЛКИ ====================
function closePlannerModal(id: string) {
    const m = document.getElementById(id);
    if (m) m.remove();
}
(window as any).closePlannerModal = closePlannerModal;

function openFindSlotModal(taskId: string) {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    const candidates = findAvailableSlots(task);

    const html = `
    <div class="wp2-modal-overlay" id="wp2-find-slot-modal">
        <div class="wp2-modal">
            <div class="wp2-modal-header">
                <h3><i class="ri-search-eye-line" style="color:var(--brand-primary)"></i> Слоти для «${task.title}»</h3>
                <button class="wp2-modal-close" onclick="closePlannerModal('wp2-find-slot-modal')"><i class="ri-close-line"></i></button>
            </div>
            <div class="wp2-modal-body">
                <div style="font-size:12px;color:var(--text-muted);display:flex;justify-content:space-between">
                    <span>Тривалість: <strong>${formatDuration(task.durationMins)}</strong></span>
                    <span>Знайдено: <strong>${candidates.length}</strong></span>
                </div>
                ${candidates.length === 0 ? `
                    <div style="text-align:center;padding:24px;color:#fca5a5">
                        <i class="ri-error-warning-line" style="font-size:28px;display:block;margin-bottom:6px"></i>
                        Немає вільного вікна на ${formatDuration(task.durationMins)}.
                        <br><span style="font-size:11px;color:var(--text-muted)">Спробуйте розділити задачу.</span>
                    </div>
                ` : candidates.map(c => `
                    <div class="wp2-candidate ${c.isPeak ? 'is-peak' : ''}">
                        <div class="wp2-candidate-info">
                            <span class="wp2-candidate-day">
                                ${c.dayName}
                                ${c.isPeak ? '<span class="wp2-tag wp2-tag-peak">⭐ Пік</span>' : ''}
                                ${c.isBuffer ? '<span class="wp2-tag wp2-tag-buffer">🌿 Буфер</span>' : ''}
                            </span>
                            <span class="wp2-candidate-time">${c.startTime} – ${c.endTime}</span>
                            <span class="wp2-candidate-desc">${c.description}</span>
                        </div>
                        <button class="wp2-btn wp2-btn-primary wp2-btn-sm" onclick="applyCandidateSlot('${task.id}', ${c.dayIndex}, '${c.startTime}', '${c.endTime}')">
                            Перенести
                        </button>
                    </div>
                `).join('')}
            </div>
        </div>
    </div>`;

    document.body.insertAdjacentHTML('beforeend', html);
}
(window as any).openFindSlotModal = openFindSlotModal;

(window as any).applyCandidateSlot = function(taskId: string, dayIndex: number, startTime: string, endTime: string) {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    task.dayIndex = dayIndex;
    task.startTime = startTime;
    task.endTime = endTime;
    task.status = 'planned';
    closePlannerModal('wp2-find-slot-modal');
    saveScheduleData();
    renderWeeklyPlanner();
    showPlannerToast(`«${task.title}» → ${DAYS_UK[dayIndex]} (${startTime}–${endTime})`, 'success');
};

(window as any).openSplitTaskModal = function(taskId: string) {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    const half = Math.floor(task.durationMins / 2 / SNAP_MINS) * SNAP_MINS;

    const html = `
    <div class="wp2-modal-overlay" id="wp2-split-modal">
        <div class="wp2-modal">
            <div class="wp2-modal-header">
                <h3><i class="ri-scissors-cut-line" style="color:var(--brand-primary)"></i> Розділити «${task.title}»</h3>
                <button class="wp2-modal-close" onclick="closePlannerModal('wp2-split-modal')"><i class="ri-close-line"></i></button>
            </div>
            <div class="wp2-modal-body">
                <p style="font-size:12px;color:var(--text-muted)">
                    Загальна тривалість: <strong>${formatDuration(task.durationMins)}</strong>. Розбийте на 2 частини.
                </p>
                <div class="wp2-form-row">
                    <div class="wp2-form-group">
                        <label class="wp2-form-label">Частина 1 (хв)</label>
                        <input type="number" id="split-p1" class="wp2-form-input" value="${half}" step="${SNAP_MINS}" min="${SNAP_MINS}" max="${task.durationMins - SNAP_MINS}">
                    </div>
                    <div class="wp2-form-group">
                        <label class="wp2-form-label">Частина 2 (хв)</label>
                        <input type="number" id="split-p2" class="wp2-form-input" value="${task.durationMins - half}" step="${SNAP_MINS}" min="${SNAP_MINS}" max="${task.durationMins - SNAP_MINS}">
                    </div>
                </div>
                <div class="wp2-form-actions">
                    <button class="wp2-btn" onclick="closePlannerModal('wp2-split-modal')">Скасувати</button>
                    <button class="wp2-btn wp2-btn-primary" onclick="confirmSplitTask('${task.id}')">Розділити</button>
                </div>
            </div>
        </div>
    </div>`;

    document.body.insertAdjacentHTML('beforeend', html);
};

(window as any).confirmSplitTask = function(taskId: string) {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    const p1 = Number((document.getElementById('split-p1') as HTMLInputElement)?.value) || 120;
    const p2 = Number((document.getElementById('split-p2') as HTMLInputElement)?.value) || (task.durationMins - p1);

    const baseTitle = task.title.replace(/ \(Частина \d+\)$/, '');

    task.durationMins = p1;
    task.title = `${baseTitle} (Частина 1)`;
    task.status = 'needs_reschedule';
    task.dayIndex = -1;

    const newTask: PlannerTask = {
        ...task,
        id: `task_${Date.now()}`,
        parentId: taskId,
        title: `${baseTitle} (Частина 2)`,
        durationMins: p2,
        dayIndex: -1,
        status: 'needs_reschedule'
    };

    tasks.push(newTask);
    closePlannerModal('wp2-split-modal');
    saveScheduleData();
    renderWeeklyPlanner();
    showPlannerToast(`Задачу розділено: ${formatDuration(p1)} + ${formatDuration(p2)}`, 'success');
};

(window as any).rescheduleWeatherTask = function(dayIndex: number) {
    const deliveryTask = tasks.find(t => t.dayIndex === dayIndex && t.category === 'delivery');
    if (!deliveryTask) {
        showPlannerToast('Доставку на цей день не знайдено', 'info');
        return;
    }
    deliveryTask.dayIndex = -1;
    deliveryTask.status = 'needs_reschedule';
    activeWeatherAlert = null;
    saveScheduleData();
    renderWeeklyPlanner();
    openFindSlotModal(deliveryTask.id);
    showPlannerToast('Доставку знято через погоду', 'warning');
};

(window as any).autoRescheduleDebtTasks = function() {
    const debtTasks = tasks.filter(t => t.dayIndex === -1 || t.status === 'needs_reschedule');
    if (debtTasks.length === 0) {
        showPlannerToast('Всі задачі заплановані!', 'info');
        return;
    }
    let count = 0;
    for (const task of debtTasks) {
        const slots = findAvailableSlots(task);
        if (slots.length > 0) {
            const best = slots[0];
            task.dayIndex = best.dayIndex;
            task.startTime = best.startTime;
            task.endTime = best.endTime;
            task.status = 'planned';
            count++;
        }
    }
    saveScheduleData();
    renderWeeklyPlanner();
    showPlannerToast(`Авто-розміщено ${count} задач`, 'success');
};

// Додавання задачі
(window as any).openQuickAddModal = function(dayIndex: number, defaultStart: string, maxMins: number) {
    const html = `
    <div class="wp2-modal-overlay" id="wp2-add-modal">
        <div class="wp2-modal">
            <div class="wp2-modal-header">
                <h3><i class="ri-add-circle-line" style="color:var(--brand-primary)"></i> Додати задачу — ${DAYS_UK[dayIndex]}</h3>
                <button class="wp2-modal-close" onclick="closePlannerModal('wp2-add-modal')"><i class="ri-close-line"></i></button>
            </div>
            <div class="wp2-modal-body">
                <div class="wp2-form-group">
                    <label class="wp2-form-label">Назва</label>
                    <input type="text" id="add-title" class="wp2-form-input" placeholder="Доставка, Спорт..." value="Доставка">
                </div>
                <div class="wp2-form-row">
                    <div class="wp2-form-group">
                        <label class="wp2-form-label">Початок</label>
                        <input type="time" id="add-start" class="wp2-form-input" value="${defaultStart}">
                    </div>
                    <div class="wp2-form-group">
                        <label class="wp2-form-label">Тривалість (хв)</label>
                        <input type="number" id="add-dur" class="wp2-form-input" value="${Math.min(240, maxMins)}" step="${SNAP_MINS}" max="${maxMins}">
                    </div>
                </div>
                <div class="wp2-form-row">
                    <div class="wp2-form-group">
                        <label class="wp2-form-label">Категорія</label>
                        <select id="add-cat" class="wp2-form-input" style="background:#18181b">
                            <option value="delivery">🚴 Доставка</option>
                            <option value="routine">🏠 Побут</option>
                            <option value="sport">🏋️ Спорт</option>
                            <option value="meal">🍽 Їжа</option>
                            <option value="school">🏫 Школа</option>
                            <option value="pum">💼 ПУМ</option>
                            <option value="other">📋 Інше</option>
                        </select>
                    </div>
                    <div class="wp2-form-group">
                        <label class="wp2-form-label">Тип</label>
                        <select id="add-fixed" class="wp2-form-input" style="background:#18181b">
                            <option value="0">Рухома</option>
                            <option value="1">Фіксована</option>
                        </select>
                    </div>
                </div>
                <div class="wp2-form-actions">
                    <button class="wp2-btn" onclick="closePlannerModal('wp2-add-modal')">Скасувати</button>
                    <button class="wp2-btn wp2-btn-primary" onclick="confirmAddTask(${dayIndex})">Додати</button>
                </div>
            </div>
        </div>
    </div>`;

    document.body.insertAdjacentHTML('beforeend', html);
};

(window as any).openAddTaskModal = function() {
    const dayIdx = selectedDayIndex >= 0 ? selectedDayIndex : (new Date().getDay() + 6) % 7;
    const freeSlots = getFreeSlotsForDay(dayIdx).filter(s => s.startMins >= 540);
    const defaultSlot = freeSlots[0];
    const defaultStart = defaultSlot ? defaultSlot.startTime : '18:30';
    const maxMins = defaultSlot ? defaultSlot.durationMins : 240;
    (window as any).openQuickAddModal(dayIdx, defaultStart, maxMins);
};

(window as any).confirmAddTask = function(dayIndex: number) {
    const title = (document.getElementById('add-title') as HTMLInputElement)?.value.trim() || 'Нова задача';
    const startTime = (document.getElementById('add-start') as HTMLInputElement)?.value || '18:30';
    const durMins = Number((document.getElementById('add-dur') as HTMLInputElement)?.value) || 60;
    const cat = ((document.getElementById('add-cat') as HTMLSelectElement)?.value || 'delivery') as any;
    const isFixed = (document.getElementById('add-fixed') as HTMLSelectElement)?.value === '1';

    const startMins = timeToMins(startTime);
    const check = canPlaceTask(dayIndex, startMins, durMins);

    if (!check.ok) {
        showPlannerToast(`Не вдалося: ${check.reason}`, 'error');
        return;
    }

    const newTask: PlannerTask = {
        id: `task_${Date.now()}`,
        dayIndex,
        startTime,
        endTime: minsToTime(startMins + durMins),
        durationMins: durMins,
        title,
        category: cat,
        isFixed,
        allowSplit: !isFixed && cat === 'delivery',
        priority: 'medium',
        status: 'planned'
    };

    tasks.push(newTask);
    closePlannerModal('wp2-add-modal');
    saveScheduleData();
    renderWeeklyPlanner();
    showPlannerToast(`«${title}» → ${DAYS_UK[dayIndex]} (${newTask.startTime}–${newTask.endTime})`, 'success');
};

(window as any).openPeakSettingsModal = function() {
    const html = `
    <div class="wp2-modal-overlay" id="wp2-peak-modal">
        <div class="wp2-modal">
            <div class="wp2-modal-header">
                <h3><i class="ri-settings-4-line" style="color:var(--brand-primary)"></i> Пікові години (Субота)</h3>
                <button class="wp2-modal-close" onclick="closePlannerModal('wp2-peak-modal')"><i class="ri-close-line"></i></button>
            </div>
            <div class="wp2-modal-body">
                <div class="wp2-form-row">
                    <div class="wp2-form-group">
                        <label class="wp2-form-label">Початок піку</label>
                        <input type="time" id="peak-start" class="wp2-form-input" value="${plannerSettings.saturdayPeakStart}">
                    </div>
                    <div class="wp2-form-group">
                        <label class="wp2-form-label">Кінець піку</label>
                        <input type="time" id="peak-end" class="wp2-form-input" value="${plannerSettings.saturdayPeakEnd}">
                    </div>
                </div>
                <div class="wp2-form-actions">
                    <button class="wp2-btn" onclick="closePlannerModal('wp2-peak-modal')">Скасувати</button>
                    <button class="wp2-btn wp2-btn-primary" onclick="savePeakSettings()">Зберегти</button>
                </div>
            </div>
        </div>
    </div>`;
    document.body.insertAdjacentHTML('beforeend', html);
};

(window as any).savePeakSettings = function() {
    plannerSettings.saturdayPeakStart = (document.getElementById('peak-start') as HTMLInputElement)?.value || '17:00';
    plannerSettings.saturdayPeakEnd = (document.getElementById('peak-end') as HTMLInputElement)?.value || '22:00';
    closePlannerModal('wp2-peak-modal');
    saveScheduleData();
    renderWeeklyPlanner();
    showPlannerToast('Пікові години оновлено', 'success');
};

// ==================== TOAST ====================
function showPlannerToast(message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info') {
    if ((window as any).showMessage) {
        (window as any).showMessage(message, type);
    } else {
        console.log(`[${type}] ${message}`);
    }
}

// ==================== ІНІЦІАЛІЗАЦІЯ ====================
(window as any).renderWeeklyPlanner = renderWeeklyPlanner;
(window as any).loadScheduleData = loadScheduleData;

document.addEventListener('DOMContentLoaded', () => {
    loadScheduleData();
});
